//
// One row's actions, behind a single unlabelled moreVert trigger and a contextual menu — the
// pattern used elsewhere in the product, replacing a dedicated "Acciones" column of standalone
// icon buttons. A row with N actions used to spend N icon-sized columns just to offer them, which
// crowded the table and forced every list to hand-roll its own anchor/Menu wiring; this is that
// wiring, written once.

import React, { useState } from 'react';
import { useTranslate } from 'react-admin';
import { Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import { MoreVertRounded as MoreIcon } from '@mui/icons-material';

export interface RowAction {
  key: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  /** Renders the item (and its icon, if any) in this palette color — e.g. 'error' for a destructive action. */
  color?: 'error' | 'warning' | 'inherit';
  /** Omit the action from the menu entirely (rather than disabling it) — e.g. a permission the current user lacks. */
  hidden?: boolean;
  /**
   * Draws a separator above this item, to set the last actions apart from the routine ones — the
   * pause before "Retire" and "Delete" that a hand-built menu drew with a `<Divider/>`. Ignored on
   * the first visible item, where a separator would have nothing above it to separate from.
   */
  dividerBefore?: boolean;
}

export interface RowActionsMenuProps {
  actions: RowAction[];
  /** Accessible label for the trigger button; defaults to the generic "more options" copy. */
  triggerLabel?: string;
}

/**
 * Renders nothing when every action is hidden — an empty menu behind a button that does nothing
 * would just be a dead click target.
 */
export const RowActionsMenu: React.FC<RowActionsMenuProps> = ({ actions, triggerLabel }) => {
  const translate = useTranslate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const visible = actions.filter((action) => !action.hidden);

  if (visible.length === 0) return null;

  const close = () => setAnchorEl(null);
  // A caller that names nothing still gets a named control: an icon-only
  // trigger without an accessible name is invisible to a screen reader and
  // unexplained to everyone else until they click it.
  const label = triggerLabel ?? translate('shell.rowActions.more');

  return (
    <>
      <Tooltip title={label}>
        <IconButton
          size="small"
          aria-label={label}
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
          }}
        >
          <MoreIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {/* Flat children, never fragments: MUI's MenuList reads its direct children to drive
            keyboard navigation, and a wrapped item drops out of the arrow-key order. */}
        {visible.flatMap((action, index) => [
          ...(action.dividerBefore && index > 0 ? [<Divider key={`${action.key}-divider`} />] : []),
          <MenuItem
            key={action.key}
            disabled={action.disabled}
            onClick={() => {
              close();
              action.onClick();
            }}
            // `.dark`, not `.main`: this is READABLE TEXT on white paper, and the
            // `.main` shades are tints meant for a fill — `error.main` #FB7185
            // measures 2.69:1 and `warning.main` #FB923C 2.26:1, both under the 3:1
            // floor at which text stops being legible at any size. The `.dark`
            // shades are 8.02:1 and 7.31:1. Every destructive row action in every
            // list went through here.
            sx={action.color ? { color: `${action.color}.dark` } : undefined}
          >
            {/* The glyph takes the same shade: an icon needs 3:1 and the tint does not reach it. */}
            {action.icon && (
              <ListItemIcon sx={action.color ? { color: `${action.color}.dark` } : undefined}>
                {action.icon}
              </ListItemIcon>
            )}
            <ListItemText>{action.label}</ListItemText>
          </MenuItem>,
        ])}
      </Menu>
    </>
  );
};

export default RowActionsMenu;
